import React, { Fragment, useMemo } from "react";
import { Row, Table } from "reactstrap";
import Select from "react-select";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { FadeLoader } from "react-spinners";
import { DebouncedInput } from "helpers/common_helper";
import Pagination from "components/Common/Pagination";

const QuestionsAndAnswersDataTable = ({
  columns,
  data,
  loading,
  totalrows,
  moduleOptions = [],
  selectedSortData,
  setSelectedSortData,
  pageIndex,
  setPageIndex,
  pageSize,
  setPageSize,
  searchString,
  setSearchString,
  selectedModule,
  setSelectedModule,
}) => {
  // ✅ Add serial column
  const columnsWithSerial = useMemo(() => {
    const serialColumn = {
      header: "#",
      id: "serial",
      cell: ({ row }) => (pageIndex - 1) * pageSize + row.index + 1,
      enableSorting: false,
      showFilter: false,
    };
    return [serialColumn, ...columns];
  }, [columns, pageIndex, pageSize]);

  const table = useReactTable({
    columns: columnsWithSerial,
    data,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalPages = Math.ceil(totalrows / pageSize);
  const handlePageChange = (newPageIndex) => setPageIndex(newPageIndex);

  return (
    <Fragment>
      {/* 🔹 Filters Row */}
      <Row className="mb-3">
        <div className="d-flex justify-content-between align-items-center flex-wrap">
          {/* 🔹 Left — Filter by Module */}
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <div style={{ width: "220px" }}>
              <Select
                placeholder="Filter by Module"
                value={selectedModule}
                onChange={setSelectedModule}
                options={moduleOptions}
                isClearable
              />
            </div>
          </div>

          {/* 🔹 Right — Search */}
          <div style={{ width: "250px" }}>
            <DebouncedInput
              value={searchString ?? ""}
              onChange={(value) => setSearchString(String(value))}
              className="form-control"
              placeholder="Search questions..."
            />
          </div>
        </div>
      </Row>

      {/* 🔹 Table */}
      <div className="table-responsive">
        <Table bordered hover>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const colDef = header.column.columnDef;
                  const isSortable = colDef.showFilter !== false; // 👈 Respect your flag

                  return (
                    <th key={header.id}>
                      <div className="d-flex align-items-center justify-content-between">
                        <span>
                          {flexRender(colDef.header, header.getContext())}
                        </span>

                        {/* ✅ Only show sort icons when showFilter !== false */}
                        {isSortable && (
                          <span
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              const field = colDef.accessorKey;
                              if (!field) return;
                              if (
                                !selectedSortData ||
                                selectedSortData.value !== field
                              )
                                setSelectedSortData({
                                  value: field,
                                  direction: "asc",
                                });
                              else if (selectedSortData.direction === "asc")
                                setSelectedSortData({
                                  value: field,
                                  direction: "desc",
                                });
                              else
                                setSelectedSortData({
                                  value: field,
                                  direction: "asc",
                                });
                            }}
                          >
                            {selectedSortData?.value === colDef.accessorKey ? (
                              selectedSortData.direction === "asc" ? (
                                <i className="mdi mdi-arrow-up"></i>
                              ) : (
                                <i className="mdi mdi-arrow-down"></i>
                              )
                            ) : (
                              <i className="mdi mdi-swap-vertical"></i>
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columnsWithSerial.length} style={{ height: "120px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <FadeLoader color="#00a895" size={40} />
                  </div>
                </td>
              </tr>
            ) : data?.length === 0 ? (
              <tr>
                <td colSpan={columnsWithSerial.length} className="text-center">
                  No Questions Found
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* 🔹 Pagination */}
      {totalrows > 0 && (
        <Pagination
          currentPage={pageIndex}
          totalPages={totalPages}
          totalItems={totalrows}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          showInfo
        />
      )}
    </Fragment>
  );
};

export default QuestionsAndAnswersDataTable;
